/**
 * Acceso encapsulado al almacenamiento del navegador.
 *
 * Regla dura del proyecto (DEC-A05): aquí NUNCA se guarda nada de la sesión.
 * Los tokens viven en cookies HttpOnly que el JavaScript no puede leer. Este
 * módulo sólo persiste preferencias de interfaz y el estado de los filtros.
 */

/** Preferencias de interfaz: sobreviven al cierre del navegador. */
export const UI_KEYS = {
  theme: 'pa.theme',
  sidebarCollapsed: 'pa.sidebar.collapsed',
} as const;

/** Estado de trabajo: vive sólo mientras la pestaña esté abierta. */
export const SESSION_KEYS = {
  filters: 'pa.filters',
} as const;

function safeRead(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeWrite(store: Storage | undefined, key: string, value: string | null): void {
  try {
    if (value === null) store?.removeItem(key);
    else store?.setItem(key, value);
  } catch {
    // Modo privado o cuota agotada: la aplicación sigue funcionando sin persistencia.
  }
}

function browserStore(kind: 'local' | 'session'): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  return kind === 'local' ? window.localStorage : window.sessionStorage;
}

export function readLocal(key: string): string | null {
  return safeRead(browserStore('local'), key);
}

export function writeLocal(key: string, value: string | null): void {
  safeWrite(browserStore('local'), key, value);
}

export function readSessionJson<T>(key: string): T | null {
  const raw = safeRead(browserStore('session'), key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeSessionJson(key: string, value: unknown): void {
  safeWrite(browserStore('session'), key, value === undefined ? null : JSON.stringify(value));
}
